import type { FeatureFlagProvider } from './core/featureFlagClient.types';

export type FeatureFlagKind = 'boolean' | 'string' | 'number';

type FeatureFlagValueByKind = {
  boolean: boolean;
  string: string;
  number: number;
};

export type FeatureFlagKey = `frontend_portal_${string}` | string;

export type PortalFeatureFlagDefinition<
  Key extends FeatureFlagKey,
  Provider extends FeatureFlagProvider = FeatureFlagProvider,
  Kind extends FeatureFlagKind = FeatureFlagKind,
> = {
  readonly key: Key;
  readonly provider: Provider;
  readonly kind: Kind;
};

export type FeatureFlagValue<
  Definition extends PortalFeatureFlagDefinition<
    FeatureFlagKey,
    FeatureFlagProvider,
    FeatureFlagKind
  >,
> = FeatureFlagValueByKind[Definition['kind']];

export type PortalFeatureFlagRegistry = Record<
  string,
  PortalFeatureFlagDefinition<FeatureFlagKey, FeatureFlagProvider, FeatureFlagKind>
>;

export function definePortalFeatureFlag<
  const Provider extends FeatureFlagProvider,
  const Name extends string,
  const Kind extends FeatureFlagKind,
>(
  provider: Provider,
  name: Name,
  kind: Kind
): PortalFeatureFlagDefinition<`frontend_portal_${Name}`, Provider, Kind> {
  return {
    key: `frontend_portal_${name}` as `frontend_portal_${Name}`,
    provider,
    kind,
  };
}

export function defineNonFrontendPortalFeatureFlag<
  const Provider extends FeatureFlagProvider,
  const Name extends string,
  const Kind extends FeatureFlagKind,
>(provider: Provider, name: Name, kind: Kind): PortalFeatureFlagDefinition<string, Provider, Kind> {
  return {
    key: name as string,
    provider,
    kind,
  };
}
